import React, { useState } from 'react';
import {
  AppBar, Toolbar, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Typography, Divider, Snackbar, Alert, Badge, Chip, Container, useMediaQuery,
  Menu, MenuItem, Avatar,
} from '@mui/material';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GroupsIcon from '@mui/icons-material/Groups';
import PaidIcon from '@mui/icons-material/Paid';
import InsightsIcon from '@mui/icons-material/Insights';
import BadgeIcon from '@mui/icons-material/Badge';
import ExploreIcon from '@mui/icons-material/Explore';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import HistoryIcon from '@mui/icons-material/History';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ChatIcon from '@mui/icons-material/Chat';

import { useApp } from '../context/AppContext';
import Logo from './Logo';
import { iniciales } from '../utils/format';

const ANCHO_MENU = 260;

const MENU_NEGOCIO = [
  { to: '/negocio', label: 'Panel', icon: <InsightsIcon />, end: true },
  { to: '/negocio/pedidos', label: 'Pedidos', icon: <ReceiptLongIcon /> },
  { to: '/negocio/catalogo', label: 'Catálogo', icon: <StorefrontIcon /> },
  { to: '/negocio/vecinos', label: 'Vecinos y fiado', icon: <GroupsIcon /> },
  { to: '/negocio/mensajes', label: 'Mensajes', icon: <ChatIcon />, badgeKey: 'mensajesNegocio' },
  { to: '/negocio/cobros', label: 'Cobros', icon: <PaidIcon /> },
  { to: '/negocio/reportes', label: 'Reportes', icon: <InsightsIcon /> },
  { to: '/negocio/perfil', label: 'Perfil del negocio', icon: <BadgeIcon /> },
];

const MENU_VECI = [
  { to: '/veci', label: 'Directorio', icon: <ExploreIcon />, end: true },
  { to: '/veci/carrito', label: 'Carrito', icon: <ShoppingBasketIcon />, badgeKey: 'carrito' },
  { to: '/veci/mensajes', label: 'Mensajes', icon: <ChatIcon />, badgeKey: 'mensajesVeci' },
  { to: '/veci/pedidos', label: 'Mis pedidos', icon: <HistoryIcon /> },
  { to: '/veci/cuenta', label: 'Mi cuenta', icon: <AccountBoxIcon /> },
];

export default function Layout() {
  const {
    sesion, sesionBackend, cerrarSesion, cerrarSesionBackend,
    negocioPorId, vecinoPorId, carrito, toast, cerrarToast,
    totalNoLeidosVeci, totalNoLeidosNegocio,
  } = useApp();
  const theme = useTheme();
  const esCompacto = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [anchorMenu, setAnchorMenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Prioriza sesión backend real. Fallback a mock para el flujo demo
  // de la landing. Ambos se resuelven a 'negocio' | 'veci'.
  const rolActivo = sesionBackend
    ? (sesionBackend.entidadTipo === 'NEGOCIO' ? 'negocio' : 'veci')
    : sesion.rol;

  const items = rolActivo === 'negocio' ? MENU_NEGOCIO : MENU_VECI;

  // Datos de la entidad activa. Backend → nombre viene del token/me,
  // sin lookup a mock. Mock legacy → busca por id.
  const negocioActivo = sesionBackend?.entidadTipo === 'NEGOCIO'
    ? { id: sesionBackend.entidadId, nombre: sesionBackend.nombre }
    : (sesion.negocioIdActivo ? negocioPorId(sesion.negocioIdActivo) : null);
  const veciActivo = sesionBackend?.entidadTipo === 'VECI'
    ? { id: sesionBackend.entidadId, nombre: sesionBackend.nombre }
    : (sesion.veciIdActivo ? vecinoPorId(sesion.veciIdActivo) : null);

  const totalCarrito = (carrito.items || []).reduce((s, it) => s + it.cantidad, 0);

  // Contadores de no leídos. Con sesionBackend real aún no hay datos —
  // las pantallas se conectarán al backend en las tandas siguientes.
  const veciIdMock = sesionBackend?.entidadTipo === 'VECI' ? null : sesion.veciIdActivo;
  const negocioIdMock = sesionBackend?.entidadTipo === 'NEGOCIO' ? null : sesion.negocioIdActivo;

  const noLeidosVeciObj = rolActivo === 'veci' && veciIdMock
    ? totalNoLeidosVeci(veciIdMock)
    : { directos: 0, anuncios: 0 };
  const noLeidosMensajesVeci = noLeidosVeciObj.directos + noLeidosVeciObj.anuncios;
  const noLeidosMensajesNegocio = rolActivo === 'negocio' && negocioIdMock
    ? totalNoLeidosNegocio(negocioIdMock)
    : 0;

  const nombrePerfil = rolActivo === 'negocio' ? negocioActivo?.nombre : veciActivo?.nombre;
  const subtituloPerfil = rolActivo === 'negocio' ? 'Perfil de negocio' : 'Perfil vecino';

  const handleCerrarSesion = async () => {
    setAnchorMenu(null);
    if (sesionBackend) {
      await cerrarSesionBackend();
      navigate('/login', { replace: true });
    } else {
      cerrarSesion();
      navigate('/');
    }
  };

  const contenidoDrawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5 }}>
        <Logo />
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Chip
          size="small"
          label={rolActivo === 'negocio' ? 'Modo negocio' : 'Modo veci'}
          sx={{
            bgcolor: rolActivo === 'negocio' ? 'primary.main' : 'warning.main',
            color: '#fff',
            fontWeight: 600,
          }}
        />
        <Typography sx={{ mt: 1.25, fontFamily: 'Fraunces', fontSize: 18, fontWeight: 600 }}>
          {nombrePerfil}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtituloPerfil}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ px: 1.5, py: 1, flexGrow: 1 }}>
        {items.map((item) => {
          let badge = 0;
          if (item.badgeKey === 'carrito') badge = totalCarrito;
          else if (item.badgeKey === 'mensajesVeci') badge = noLeidosMensajesVeci;
          else if (item.badgeKey === 'mensajesNegocio') badge = noLeidosMensajesNegocio;
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              end={item.end}
              onClick={() => setDrawerAbierto(false)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                color: 'text.primary',
                '&.active': {
                  bgcolor: 'rgba(15, 76, 58, 0.08)',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {badge > 0 ? (
                  <Badge badgeContent={badge} color="warning">{item.icon}</Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <ListItemButton onClick={handleCerrarSesion} sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Salir" />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Título de la sección actual, para el AppBar móvil
  const rutaActual = items.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));
  const tituloSeccion = rutaActual?.label || 'HolaVeci';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Drawer permanente en desktop */}
      {!esCompacto && (
        <Drawer
          variant="permanent"
          sx={{
            width: ANCHO_MENU,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: ANCHO_MENU,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
          open
        >
          {contenidoDrawer}
        </Drawer>
      )}

      {/* Drawer temporal en móvil */}
      {esCompacto && (
        <Drawer
          variant="temporary"
          open={drawerAbierto}
          onClose={() => setDrawerAbierto(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: ANCHO_MENU, boxSizing: 'border-box' },
          }}
        >
          {contenidoDrawer}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 1 }}>
            {esCompacto && (
              <IconButton
                edge="start"
                onClick={() => setDrawerAbierto(true)}
                aria-label="Abrir menú"
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ flexGrow: 1, fontFamily: 'Fraunces' }}>
              {tituloSeccion}
            </Typography>
            <IconButton
              onClick={(e) => setAnchorMenu(e.currentTarget)}
              aria-label="Cuenta"
              size="small"
            >
              <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: 14 }}>
                {iniciales(nombrePerfil || 'H V')}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorMenu}
              open={Boolean(anchorMenu)}
              onClose={() => setAnchorMenu(null)}
            >
              <MenuItem disabled sx={{ opacity: 1, '&.Mui-disabled': { opacity: 1 } }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{nombrePerfil}</Typography>
                  <Typography variant="caption" color="text.secondary">{subtituloPerfil}</Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleCerrarSesion}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Cambiar de perfil
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
          <Container maxWidth="lg">
            <Outlet />
          </Container>
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        // Los errores necesitan tiempo para leer y decidir qué hacer.
        // Los éxitos son confirmaciones rápidas que ya no requieren accion.
        autoHideDuration={toast.severity === 'error' ? 8000 : 2800}
        onClose={cerrarToast}
        // Arriba-centro para que no lo tape un Dialog abierto (que suele
        // ocupar el centro-abajo de la pantalla).
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={cerrarToast} severity={toast.severity} variant="filled"
          sx={{ borderRadius: 2, minWidth: 300, fontSize: 15 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
